"use client";

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as React from "react";
import { cn } from "../utils/classnames";
import { Button } from "./Button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api?: CarouselApi) => void;
  canDrag?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      canDrag = true,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        // containScroll: "trimSnaps",
        align: "start",
        axis: orientation === "horizontal" ? "x" : "y",
        draggable: canDrag,
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canGoToPrev());
      setCanScrollNext(api.canGoToNext());
      setSelectedIndex(api.selectedSnap());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.goToPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.goToNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      setScrollSnaps(api.snapList());
      api.on("reinit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
        api?.off("reinit", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          api: api,
          canScrollNext,
          canScrollPrev,
          carouselRef,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollNext,
          scrollPrev,
          scrollSnaps,
          selectedIndex,
        }}
      >
        <div
          aria-roledescription="carousel"
          className={cn(
            "relative overflow-visible",
            "w-full", // todo add this in
            className,
          )}
          onKeyDownCapture={handleKeyDown}
          ref={ref}
          role="region"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div className={"overflow-x-hidden"} ref={carouselRef}>
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "" : "-mt-4 flex-col",
          className,
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      aria-roledescription="item"
      className={cn(
        "min-w-0 shrink-0 grow-0",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      ref={ref}
      role="group"
      {...props}
    />
  );
});
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      className={cn("h-8 w-8 rounded-full", className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      ref={ref}
      size={size}
      variant={variant}
      {...props}
    >
      {variant !== "dot" && <ArrowLeft className="h-4 w-4" />}
      <span className="sr-only">Previous item</span>
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      className={cn("h-8 w-8 rounded-full", className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      ref={ref}
      size={size}
      variant={variant}
      {...props}
    >
      {variant !== "dot" && <ArrowRight className="h-4 w-4" />}
      <span className="sr-only">Next item</span>
    </Button>
  );
});
CarouselNext.displayName = "CarouselNext";

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { scrollSnaps } = useCarousel();

  return (
    <div
      className={cn("mt-4 flex justify-center gap-1", className)}
      ref={ref}
      {...props}
    >
      {scrollSnaps.map((_, index) => (
        <CarouselDot index={index} key={index} />
      ))}
    </div>
  );
});
CarouselDots.displayName = "CarouselDots";

type CarouselArrowsProps = React.HTMLAttributes<HTMLDivElement> & {
  // There's no reliable way to get the total number of items in the carousel,
  // so we pass the total length of reviews as a prop.
  reviewsLength: number;
};

const CarouselArrows = React.forwardRef<HTMLDivElement, CarouselArrowsProps>(
  ({ className, reviewsLength }, ref) => {
    const { selectedIndex } = useCarousel();

    return (
      <div
        className={cn(
          "my-4 hidden items-center justify-center gap-4 lg:flex",
          className,
        )}
        ref={ref}
      >
        <CarouselPrevious />
        <span>
          {Math.min(selectedIndex + 1, reviewsLength)} /{reviewsLength}
        </span>
        <CarouselNext />
      </div>
    );
  },
);
CarouselArrows.displayName = "CarouselArrows";

const CarouselDot = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { index: number }
>(({ className, index, ...props }, ref) => {
  const { api, selectedIndex } = useCarousel();

  const handleClick = React.useCallback(() => {
    api?.goTo(index);
  }, [api, index]);

  return (
    <Button
      className={cn(
        "h-2 w-2 rounded-full p-0",
        selectedIndex === index ? "bg-primary" : "bg-muted",
        className,
      )}
      onClick={handleClick}
      ref={ref}
      size="sm"
      variant="outline"
      {...props}
    />
  );
});
CarouselDot.displayName = "CarouselDot";

export {
  Carousel,
  type CarouselApi,
  CarouselArrows,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
};
